import type { CanvasManager } from '../core/CanvasManager';
import type { CropRect } from '../core/types';
import { clamp } from '../processing/clamp';

type HandlePos = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

const HANDLE_SIZE = 8;

export class CropOverlay {
  private cm: CanvasManager;
  private rect: CropRect = { x: 0, y: 0, width: 0, height: 0 };
  private dragging: 'move' | HandlePos | null = null;
  private dragStart = { x: 0, y: 0 };
  private rectStart: CropRect = { x: 0, y: 0, width: 0, height: 0 };
  private active = false;
  private applyBtn: HTMLElement;
  private cancelBtn: HTMLElement;
  private onApply: (rect: CropRect) => void;
  private onCancel: () => void;

  private boundMouseDown = this.handleMouseDown.bind(this);
  private boundMouseMove = this.handleMouseMove.bind(this);
  private boundMouseUp = this.handleMouseUp.bind(this);

  constructor(
    cm: CanvasManager,
    applyBtn: HTMLElement,
    cancelBtn: HTMLElement,
    onApply: (rect: CropRect) => void,
    onCancel: () => void,
  ) {
    this.cm = cm;
    this.applyBtn = applyBtn;
    this.cancelBtn = cancelBtn;
    this.onApply = onApply;
    this.onCancel = onCancel;

    this.applyBtn.addEventListener('click', () => this.apply());
    this.cancelBtn.addEventListener('click', () => this.cancel());
  }

  activate(): void {
    if (this.active) return;
    this.active = true;

    // Default crop: 80% centered
    const w = this.cm.canvas.width;
    const h = this.cm.canvas.height;
    const margin = 0.1;
    this.rect = {
      x: Math.round(w * margin),
      y: Math.round(h * margin),
      width: Math.round(w * (1 - 2 * margin)),
      height: Math.round(h * (1 - 2 * margin)),
    };

    this.cm.showOverlay();
    this.applyBtn.classList.remove('hidden');
    this.cancelBtn.classList.remove('hidden');
    this.applyBtn.parentElement?.classList.remove('hidden');
    this.draw();

    this.cm.overlay.addEventListener('mousedown', this.boundMouseDown);
    document.addEventListener('mousemove', this.boundMouseMove);
    document.addEventListener('mouseup', this.boundMouseUp);
  }

  deactivate(): void {
    this.active = false;
    this.dragging = null;
    this.cm.hideOverlay();
    this.applyBtn.parentElement?.classList.add('hidden');
    this.cm.overlay.removeEventListener('mousedown', this.boundMouseDown);
    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);
  }

  get isActive(): boolean {
    return this.active;
  }

  private draw(): void {
    const ctx = this.cm.overlayCtx;
    const w = this.cm.overlay.width;
    const h = this.cm.overlay.height;
    const { x, y, width, height } = this.rect;

    ctx.clearRect(0, 0, w, h);

    // Dark mask outside crop
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, w, h);
    ctx.clearRect(x, y, width, height);

    // Border
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, width, height);

    // Rule of thirds grid
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1;
    for (let i = 1; i <= 2; i++) {
      const gx = x + (width * i) / 3;
      const gy = y + (height * i) / 3;
      ctx.beginPath();
      ctx.moveTo(gx, y);
      ctx.lineTo(gx, y + height);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x, gy);
      ctx.lineTo(x + width, gy);
      ctx.stroke();
    }

    // 8 resize handles
    ctx.fillStyle = '#fff';
    const handles = this.getHandlePositions();
    for (const pos of handles) {
      ctx.fillRect(pos.hx - HANDLE_SIZE / 2, pos.hy - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
    }
  }

  private getHandlePositions(): { handle: HandlePos; hx: number; hy: number }[] {
    const { x, y, width: w, height: h } = this.rect;
    const cx = x + w / 2;
    const cy = y + h / 2;
    return [
      { handle: 'nw', hx: x, hy: y },
      { handle: 'n', hx: cx, hy: y },
      { handle: 'ne', hx: x + w, hy: y },
      { handle: 'e', hx: x + w, hy: cy },
      { handle: 'se', hx: x + w, hy: y + h },
      { handle: 's', hx: cx, hy: y + h },
      { handle: 'sw', hx: x, hy: y + h },
      { handle: 'w', hx: x, hy: cy },
    ];
  }

  private hitTest(ix: number, iy: number): 'move' | HandlePos | null {
    // Check handles first
    const handles = this.getHandlePositions();
    const threshold = HANDLE_SIZE * 2; // generous hit target
    for (const { handle, hx, hy } of handles) {
      if (Math.abs(ix - hx) < threshold && Math.abs(iy - hy) < threshold) {
        return handle;
      }
    }
    // Check inside rect
    const { x, y, width, height } = this.rect;
    if (ix >= x && ix <= x + width && iy >= y && iy <= y + height) {
      return 'move';
    }
    return null;
  }

  private handleMouseDown(e: Event): void {
    const me = e as MouseEvent;
    const { x, y } = this.cm.toImageCoords(me.clientX, me.clientY);
    const hit = this.hitTest(x, y);
    if (!hit) return;

    this.dragging = hit;
    this.dragStart = { x, y };
    this.rectStart = { ...this.rect };
    me.preventDefault();
  }

  private handleMouseMove(e: Event): void {
    const me = e as MouseEvent;
    if (!this.dragging) {
      // Update cursor
      const { x, y } = this.cm.toImageCoords(me.clientX, me.clientY);
      const hit = this.hitTest(x, y);
      this.cm.overlay.style.cursor = this.getCursor(hit);
      return;
    }

    const { x, y } = this.cm.toImageCoords(me.clientX, me.clientY);
    const dx = x - this.dragStart.x;
    const dy = y - this.dragStart.y;
    const maxW = this.cm.canvas.width;
    const maxH = this.cm.canvas.height;
    const MIN_SIZE = 10;

    if (this.dragging === 'move') {
      this.rect.x = clamp(this.rectStart.x + dx, 0, maxW - this.rect.width);
      this.rect.y = clamp(this.rectStart.y + dy, 0, maxH - this.rect.height);
    } else {
      const r = this.rectStart;
      let nx = r.x, ny = r.y, nw = r.width, nh = r.height;

      // Horizontal
      if (this.dragging.includes('w')) {
        const newX = clamp(r.x + dx, 0, r.x + r.width - MIN_SIZE);
        nw = r.width + (r.x - newX);
        nx = newX;
      } else if (this.dragging.includes('e')) {
        nw = clamp(r.width + dx, MIN_SIZE, maxW - r.x);
      }

      // Vertical
      if (this.dragging.includes('n')) {
        const newY = clamp(r.y + dy, 0, r.y + r.height - MIN_SIZE);
        nh = r.height + (r.y - newY);
        ny = newY;
      } else if (this.dragging.includes('s')) {
        nh = clamp(r.height + dy, MIN_SIZE, maxH - r.y);
      }

      this.rect = { x: nx, y: ny, width: nw, height: nh };
    }

    this.draw();
  }

  private handleMouseUp(): void {
    this.dragging = null;
  }

  private getCursor(hit: 'move' | HandlePos | null): string {
    switch (hit) {
      case 'nw': case 'se': return 'nwse-resize';
      case 'ne': case 'sw': return 'nesw-resize';
      case 'n': case 's': return 'ns-resize';
      case 'e': case 'w': return 'ew-resize';
      case 'move': return 'move';
      default: return 'crosshair';
    }
  }

  private apply(): void {
    const rect = { ...this.rect };
    this.deactivate();
    this.onApply(rect);
  }

  private cancel(): void {
    this.deactivate();
    this.onCancel();
  }
}
